import { Trash } from '@phosphor-icons/react';
import { Fragment, useState } from 'react';
import translations from '../../translations';
import type { TenantIntegrationAccountAPI } from '../../types/Tenant';
import ButtonPrimary from '../buttons/ButtonPrimary';
import Table, { type TableColumnList } from '../generic/Table';
import AddIntegrationAccountModal from './AddIntegrationAccountModal';
import type { UpdateTenant } from './TenantPage';

type IntegrationAccountsProps = {
    integrationAccounts: TenantIntegrationAccountAPI[];
    updateTenant: UpdateTenant;
};

const IntegrationAccounts = ({ integrationAccounts, updateTenant }: IntegrationAccountsProps) => {
    const [showAddModal, setShowAddModal] = useState(false);

    // biome-ignore lint/suspicious/noArrayIndexKey: No alternative to index
    const bullets = new Array(6).map((_, i) => <Fragment key={i}>&bull;</Fragment>);

    const handleDelete = (id: string) => {
        const updatedAccounts = integrationAccounts.filter(({ accountId }) => accountId !== id);

        updateTenant({
            tenantSettings: {
                integrationAccounts: updatedAccounts,
            },
        });
    };

    const columns: TableColumnList<TenantIntegrationAccountAPI> = [
        {
            renderHeader: () => translations.TENANT_integration_accounts_username,
            renderCell: ({ item }) => item.username,
        },
        {
            renderHeader: () => translations.TENANT_integration_accounts_account_id,
            renderCell: ({ item }) => item.accountId,
        },
        {
            renderHeader: () => translations.TENANT_integration_accounts_api_key,
            renderCell: ({ item }) => (
                <Fragment key={item.accountId}>
                    {item.apiKey}
                    {bullets}
                </Fragment>
            ),
        },
        {
            renderHeader: () => 'Actions',
            renderCell: ({ item }) => (
                <div className="action-btn-wrapper">
                    <button
                        title={translations.COMMON_delete}
                        className="table-icon table-icon-delete"
                        onClick={() => handleDelete(item.accountId)}
                        data-testid="integration-account-delete"
                        type="button"
                    >
                        <Trash />
                    </button>
                </div>
            ),
            size: '5rem',
        },
    ];

    const onAdd = () => setShowAddModal(true);
    const onClose = () => setShowAddModal(false);

    const onAdded = (username: string, accountId: string, apiKey: string) => {
        setShowAddModal(false);

        const newAccount: TenantIntegrationAccountAPI = {
            username,
            accountId,
            apiKey: apiKey.substring(0, 6),
        };

        updateTenant({
            tenantSettings: {
                integrationAccounts: [...integrationAccounts, newAccount],
            },
        });
    };

    return (
        <>
            <ButtonPrimary onClick={onAdd}>
                {translations.TENANT_integration_accounts_add}
            </ButtonPrimary>
            {integrationAccounts.length > 0 ? (
                <Table<TenantIntegrationAccountAPI>
                    tableClass="generic-table margin-top"
                    items={integrationAccounts}
                    columns={columns}
                />
            ) : null}
            <AddIntegrationAccountModal show={showAddModal} onAdded={onAdded} onClose={onClose} />
        </>
    );
};

export default IntegrationAccounts;
